import { motion, useMotionValue, useTransform, useReducedMotion } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { scrollToId, waSitesLink, WA_MESSAGES } from "../config/site";
import { trackEvent } from "../lib/analytics";
import { Overline } from "./Reveal";
import { Magnetic } from "./Magnetic";

const EASE = [0.16, 1, 0.3, 1];
const T = 1.05; // cortina rápida do preloader

const SERVICES_ROW = ["Criação de Sites", "Landing Pages", "Tráfego Pago", "Estratégia Digital"];

export const Hero = () => {
  const reduce = useReducedMotion();
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const orbX = useTransform(mx, (v) => v * 26);
  const orbY = useTransform(my, (v) => v * 18);
  const logoX = useTransform(mx, (v) => v * -14);
  const logoY = useTransform(my, (v) => v * -10);

  const onMouseMove = (e) => {
    if (reduce) return;
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      id="inicio"
      data-testid="hero-section"
      onMouseMove={onMouseMove}
      className="relative flex min-h-screen items-center overflow-hidden pt-28 pb-24"
    >
      <div className="grid-bg absolute inset-0" aria-hidden="true" />
      <motion.div
        aria-hidden="true"
        initial={{ x: "-130%" }}
        animate={reduce ? {} : { x: "230%" }}
        transition={{ delay: T - 0.05, duration: 1.6, ease: "easeInOut" }}
        className="pointer-events-none absolute inset-y-[-20%] left-0 z-20 w-[45%] rotate-12 bg-gradient-to-r from-transparent via-white/[0.08] to-transparent"
      />
      <motion.div
        aria-hidden="true"
        style={reduce ? {} : { x: orbX, y: orbY }}
        className="absolute -top-32 left-[8%] h-[480px] w-[480px] rounded-full bg-[radial-gradient(circle,rgba(199,205,214,0.10),transparent_65%)] blur-2xl"
      />
      <div aria-hidden="true" className="absolute bottom-[-25%] right-[-8%] h-[520px] w-[520px] rounded-full bg-[radial-gradient(circle,rgba(199,205,214,0.08),transparent_62%)] blur-3xl" />

      <div className="relative z-10 mx-auto grid w-full max-w-7xl items-center gap-16 px-4 sm:px-6 lg:grid-cols-[1.12fr_0.88fr] lg:px-12">
        <div>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: T, ease: EASE }}>
            <Overline>Estratégia digital para negócios que querem evoluir</Overline>
          </motion.div>

          <h1 className="mt-7 font-display text-[1.72rem] font-extrabold leading-[1.1] tracking-tight sm:text-5xl lg:text-[2.85rem] xl:text-[3.35rem]">
            {[
              <>SEU PRÓXIMO NÍVEL</>,
              <>COMEÇA COM UMA</>,
              <>
                <span className="chrome-text">ESTRATÉGIA DIGITAL</span>
              </>,
              <>INTELIGENTE.</>,
            ].map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  initial={reduce ? false : { y: "112%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1, delay: T + 0.12 + i * 0.13, ease: EASE }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: T + 0.75, ease: EASE }}
            className="mt-7 max-w-xl text-base leading-relaxed text-gb-gray sm:text-lg"
          >
            Criamos sites profissionais, landing pages e estratégias de tráfego pago que transformam
            sua presença digital em oportunidades reais de crescimento.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: T + 0.9, ease: EASE }}
            className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center"
          >
            <Magnetic>
              <a
                href={waSitesLink(WA_MESSAGES.site_que_vende)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent("whatsapp_click", { location: "hero" })}
                data-testid="hero-whatsapp-btn"
                className="btn-chrome group inline-flex items-center justify-center gap-2 rounded-full px-8 py-4 text-sm font-bold tracking-wide"
              >
                QUERO UM SITE QUE VENDE
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-1 group-hover:translate-x-1" aria-hidden="true" />
              </a>
            </Magnetic>
            <button
              type="button"
              onClick={() => scrollToId("#portfolio")}
              data-testid="hero-solutions-btn"
              className="btn-ghost-chrome inline-flex items-center justify-center gap-2 rounded-full px-8 py-4 text-sm font-semibold tracking-wide text-gb-white"
            >
              CONHECER NOSSOS PROJETOS
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: T + 1.15 }}
            className="mt-12 flex flex-wrap items-center gap-x-5 gap-y-2"
          >
            {SERVICES_ROW.map((s, i) => (
              <span key={s} className="flex items-center gap-5 font-mono text-[10px] uppercase tracking-[0.26em] text-gb-gray">
                {s}
                {i < SERVICES_ROW.length - 1 && <span className="h-1 w-1 rounded-full bg-gb-silver/70" aria-hidden="true" />}
              </span>
            ))}
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, delay: T + 0.3, ease: EASE }}
          className="relative order-first mx-auto -mt-4 mb-2 flex w-full max-w-[240px] items-center justify-center sm:max-w-[300px] lg:order-none lg:m-0 lg:max-w-[400px] xl:max-w-[450px]"
        >
          <div aria-hidden="true" className="absolute inset-0 m-auto h-[74%] w-[74%] rounded-full bg-[radial-gradient(circle,rgba(199,205,214,0.17),transparent_65%)] blur-2xl" />
          <motion.div
            style={reduce ? {} : { x: logoX, y: logoY }}
            className="relative flex items-center justify-center"
          >
            <motion.span
              aria-hidden="true"
              initial={{ scale: 0.85, opacity: 0.7 }}
              animate={{ scale: 1.35, opacity: 0 }}
              transition={{ delay: T + 0.45, duration: 1.3, ease: "easeOut" }}
              className="absolute inset-0 rounded-full border border-gb-silver/50"
            />
            <motion.div
              animate={reduce ? {} : { y: [0, -14, 0] }}
              transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
              className="relative flex items-center justify-center"
            >
              {/* anéis giroscópicos 3D */}
              <div aria-hidden="true" className="gyro hidden sm:block">
                <div className="gyro-plane" style={{ "--tilt": "64deg" }}>
                  <div className="gyro-ring" style={{ "--speed": "15s" }} />
                  <div className="gyro-dot-wrap" style={{ "--speed": "15s" }}>
                    <span className="gyro-dot" />
                  </div>
                </div>
              </div>
              <div aria-hidden="true" className="gyro hidden sm:block" style={{ inset: "-24%" }}>
                <div className="gyro-plane" style={{ "--tilt": "74deg" }}>
                  <div className="gyro-ring" style={{ "--speed": "24s", "--dir": "reverse", opacity: 0.6 }} />
                  <div className="gyro-dot-wrap" style={{ "--speed": "24s", "--dir": "reverse" }}>
                    <span className="gyro-dot" style={{ opacity: 0.7, height: "5px", width: "5px" }} />
                  </div>
                </div>
              </div>

              {/* anel de texto 2D */}
              <svg viewBox="0 0 400 400" className="absolute h-[130%] w-[130%] animate-spin-slow" aria-hidden="true">
                <defs>
                  <path id="heroCircleText" d="M 200,200 m -172,0 a 172,172 0 1,1 344,0 a 172,172 0 1,1 -344,0" />
                </defs>
                <text className="fill-gb-silver/60 font-mono" style={{ fontSize: "13.5px", letterSpacing: "0.42em" }}>
                  <textPath href="#heroCircleText">
                    TRÁFEGO PAGO · LANDING PAGES · DESENVOLVIMENTO · OTIMIZAÇÕES ·
                  </textPath>
                </text>
              </svg>

              {/* logo com iluminação progressiva */}
              <motion.img
                src="/assets/logo.jpg"
                alt="Emblema GOBAX — Tráfego e Sites"
                width={560}
                height={560}
                fetchpriority="high"
                data-testid="hero-logo"
                initial={reduce ? false : { filter: "brightness(0.28) saturate(0.8)", scale: 0.96 }}
                animate={{ filter: "brightness(1) saturate(1)", scale: 1 }}
                transition={{ delay: T + 0.75, duration: 1.6, ease: EASE }}
                className="relative z-10 aspect-square w-full rounded-full object-cover ring-1 ring-white/20 shadow-[0_40px_120px_-30px_rgba(199,205,214,0.35)]"
              />
              <span aria-hidden="true" className="logo-sheen z-20" />
            </motion.div>
          </motion.div>
        </motion.div>
      </div>

      <motion.button
        type="button"
        onClick={() => scrollToId("#solucoes")}
        data-testid="hero-scroll-indicator"
        aria-label="Rolar para a próxima seção"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: T + 1.5, duration: 1 }}
        className="absolute bottom-7 left-1/2 z-10 -translate-x-1/2 text-gb-gray transition-colors hover:text-gb-white"
      >
        <motion.span animate={reduce ? {} : { y: [0, 8, 0] }} transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }} className="flex flex-col items-center gap-2">
          <span className="font-mono text-[9px] uppercase tracking-[0.3em]">Explore</span>
          <ArrowDown className="h-4 w-4" aria-hidden="true" />
        </motion.span>
      </motion.button>
    </section>
  );
};
